import { cn } from '../lib/cn';

export interface ISegment<TValue extends string> {
  value: TValue;
  label: string;
  count?: number;
  /** Shown but not choosable — a mode that exists and is not open yet. */
  disabled?: boolean;
}

interface ISegmentedProps<TValue extends string> {
  segments: readonly ISegment<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  fill?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const Segmented = <TValue extends string>({
  segments,
  value,
  onChange,
  fill,
  className,
  ...rest
}: ISegmentedProps<TValue>) => (
  <div
    role="radiogroup"
    className={cn(
      'inline-flex gap-1 rounded-panel bg-segment-track p-1',
      fill && 'flex w-full',
      className,
    )}
    {...rest}
  >
    {segments.map((segment) => (
      <button
        key={segment.value}
        type="button"
        role="radio"
        aria-checked={segment.value === value}
        disabled={segment.disabled}
        onClick={() => onChange(segment.value)}
        className={cn(
          'flex h-control-xs min-w-0 items-center justify-center gap-label',
          'rounded-item px-2 text-meta font-medium',
          fill && 'flex-1',
          segment.value === value
            ? 'bg-highlight text-primary'
            : 'text-muted enabled:hover:text-primary disabled:text-faint',
        )}
      >
        <span className="truncate">{segment.label}</span>
        {segment.count !== undefined && (
          <span className="shrink-0 font-mono text-micro text-faint">{segment.count}</span>
        )}
      </button>
    ))}
  </div>
);
